import React, { useEffect, useMemo, useState, useCallback } from 'react';

import { Avatar, Col, Row, Space, Tooltip } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';

import { formatTZLocal, translate } from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import styles from './style.less';
import UserPicture from '../UserPicture';

const {
  ActionUserInstanceUserOperationType,
  ActionUserInstanceUserOperationTypeStatus,
  ProcessInstanceApprovalOriginStatus,
} = CoreEnums;

const UserAvatar = (props) => {
  const [name, setName] = useState('');

  const isSystem = useMemo(
    () => props.operationType === ActionUserInstanceUserOperationType.OverdueDateTimeReached,
    [props.operationType],
  );

  const sizeStyle = useMemo(() => {
    if (!props.size) return {};
    if (props.styles) return props.styles;
    return props.iconOnly ? { width: props.size } : {};
  }, [props.size, props.iconOnly, props.styles]);

  useEffect(() => {
    const matches = [
      props.fullName,
      props.firstName && props.lastName && `${props.firstName} ${props.lastName}`,
      props.user?.fullName || props.externalUserEmail,
      props.name,
      props.userFullName,
      props.username,
      props.userCreatorName,
    ].filter(Boolean);

    setName(_.first(matches));
  }, [props]);

  const showObservationText = useCallback(() => {
    return props.operationType === ActionUserInstanceUserOperationType.Approved ||
      props.operationType === ActionUserInstanceUserOperationType.Rejected
      ? translate(`global.observation.type${props.operationType}`)
      : translate(`global.observation.type0`);
  }, [props.operationType]);

  const showOperationType = useCallback(() => {
    if (!props.operationType) return null;
    const opsType = ActionUserInstanceUserOperationTypeStatus[props.operationType];
    return (
      <Row gutter={8}>
        {opsType && (
          <Col>
            <div className={classNames(styles.badge, opsType)}>
              {translate(`global.text.${opsType}`)}
            </div>
          </Col>
        )}
        {props.created && (
          <Col>
            <span className={styles.created}>{formatTZLocal(props.created, 'L H:mm a')}</span>
          </Col>
        )}
      </Row>
    );
  }, [props.created, props.operationType]);

  const showProcessInstanceName = useCallback(() => {
    if (!props.processInstanceName) return null;
    return (
      <Space size={4}>
        <span>{translate('applications.detail.application')}:</span>
        {props.processInstanceId ? (
          <a href={`/applications/${props.processInstanceId}`} rel="noreferrer" target="_blank">
            <span>{props.processInstanceName}</span>
          </a>
        ) : (
          <span>{props.processInstanceName}</span>
        )}
      </Space>
    );
  }, [props.processInstanceId, props.processInstanceName]);

  const showDisplayName = useCallback(() => {
    const isOrigin = props.approvalOrigin !== 0 && props.approvalOrigin != null;
    const origin = ProcessInstanceApprovalOriginStatus[props.approvalOrigin];
    return (
      <span
        className={classNames(styles.name, {
          [styles.origin]: isOrigin,
          [styles.agent]: props.isAgent,
        })}
      >
        {showProcessInstanceName()}
        {_.isNil(props.truncate) ? name : _.truncate(name, { length: 25 })}
        {isOrigin && <span>({translate(`global.text.${origin}`)})</span>}
        {props.isAgent && <span>({translate(`global.text.agent`)})</span>}
      </span>
    );
  }, [name, props.approvalOrigin, props.isAgent, props.truncate, showProcessInstanceName]);

  const showObservations = useCallback(() => {
    if (!props.observations) return null;
    return (
      <div className={styles.observations}>
        <strong>{showObservationText()}:</strong>
        <span>{props.observations}</span>
      </div>
    );
  }, [props.observations, showObservationText]);

  return (
    <Tooltip title={name}>
      <div className={styles.userAvatar} style={sizeStyle}>
        <Space
          size={8}
          className={classNames(styles.space, { [styles.composite]: props.operationType })}
          style={sizeStyle}
        >
          {!props.isUser && !props.isAgent && props.iconProps && (
            <Avatar {...props.iconProps} className={styles.iconTypeAvatar} size={props.size} />
          )}

          {props.isUser && (name || props.id) && !props.processInstanceName && !isSystem && (
            <UserPicture
              {...props}
              {...props.user}
              showTooltip={false}
              styles={sizeStyle}
              isAgent={props.isAgent}
            />
          )}

          {props.renderName ? props.renderName : null}
          {props.iconOnly || props.renderName ? null : (
            <div className={styles.container} style={isSystem ? { display: 'inline-block' } : {}}>
              {showDisplayName()}
              {showOperationType()}
              {showObservations()}
            </div>
          )}
        </Space>
      </div>
    </Tooltip>
  );
};

UserAvatar.defaultProps = {
  isUser: true,
  iconOnly: false,
  isAgent: false,
  size: 35,
};

export default UserAvatar;
